import { type ComponentType } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ToastProvider } from '@/contexts/ToastContext';
import { ModalProvider } from '@/contexts/ModalContext';
import { PubNubProvider } from '@/contexts/PubNubContext';
import { ToastContainer } from '@/components/ToastContainer';
import AppRouter from '@/router/AppRouter';

// ── Root management ────────────────────────────────────────────────────

const rootMap = new WeakMap<Element, Root>();

/**
 * Wrapper for the SINGLE global app mount. Registers app-wide singletons
 * (ModalProvider's imperativeConfirm/Prompt, ToastProvider's imperativeAddToast)
 * and renders the ToastContainer. Should be used exactly once per app
 * lifetime — via mountGlobalProviders / mountGlobalProvidersWithRouter.
 */
function GlobalProviders({ children }: { children: React.ReactNode }) {
  return (
    <ThemeProvider>
      <ToastProvider>
        <ModalProvider>
          <PubNubProvider>
            {children}
            <ToastContainer />
          </PubNubProvider>
        </ModalProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

/**
 * Wrapper for imperatively-mounted components rendered outside the main
 * React tree (renderImperativeModal, renderReactComponent for legacy bridges).
 *
 * Intentionally minimal: only includes providers that supply per-subtree
 * context (theme) without registering app-wide singletons. Using the full
 * GlobalProviders here would create duplicate ModalProvider/ToastProvider
 * instances that overwrite the main app's singleton registrations on mount
 * and null them out on unmount — silently breaking confirm/prompt/toast
 * for the rest of the app.
 *
 * Imperative subtrees can still reach the singletons via the OrnaReact
 * imperative API (OrnaReact.confirm / OrnaReact.showToast), which routes
 * to the main app's registered functions.
 */
function ImperativeProviders({ children }: { children: React.ReactNode }) {
  return (
    <ThemeProvider>
      {children}
    </ThemeProvider>
  );
}

/**
 * Mount a React component into a DOM container, wrapping it in
 * ThemeProvider / ToastProvider / ModalProvider.
 *
 * Mirrors the existing `new Vue({ el: container, ... })` pattern so that
 * management modules (CallCards.ts, CaseReportManagement.ts, etc.) can
 * mount React components the same way they currently mount Vue instances.
 */
export function renderReactComponent<P extends Record<string, any>>(
  Component: ComponentType<P>,
  props: P,
  container: Element
): Root {
  // Unmount any existing root on this container
  destroyReactComponent(container);

  // Remove Vue's v-cloak attribute so CSS doesn't hide the container
  container.removeAttribute('v-cloak');

  const root = createRoot(container);
  rootMap.set(container, root);

  root.render(
    <ImperativeProviders>
      <Component {...props} />
    </ImperativeProviders>
  );

  return root;
}

/**
 * Unmount a React component previously mounted with `renderReactComponent`.
 * Safe to call even if no component is mounted.
 */
export function destroyReactComponent(container: Element): void {
  const root = rootMap.get(container);
  if (root) {
    root.unmount();
    rootMap.delete(container);
  }
}

// ── Global providers root ──────────────────────────────────────────────

let globalRoot: Root | null = null;

/**
 * Mount the global React providers (Theme, Toast, Modal) into a container.
 * Called once at app startup from main.tsx.
 * This provides the Toast container and registers the imperative modal/toast APIs
 * so non-React code can call `OrnaReact.confirm()`, `OrnaReact.showToast()`, etc.
 */
export function mountGlobalProviders(container: Element): Root {
  globalRoot = createRoot(container);
  globalRoot.render(
    <GlobalProviders>
      {/* Empty — providers + ToastContainer are the only children needed */}
      <></>
    </GlobalProviders>
  );
  return globalRoot;
}

/**
 * Mount the global React providers with React Router (BrowserRouter) into a container.
 * Called at app startup when React Router is the active routing engine.
 * The AppRouter renders RouteHandler which replicates GeneralPathing behavior.
 */
export function mountGlobalProvidersWithRouter(container: Element): Root {
  globalRoot = createRoot(container);
  globalRoot.render(
    <GlobalProviders>
      <AppRouter />
    </GlobalProviders>
  );
  return globalRoot;
}
